import { DialogForm, Label, useToast } from '@ais-ui/ui-core';
import { ReportTemplate, useDeleteReportTemplate } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface ReportTemplateDeleteProps {
  reportTemplate: ReportTemplate;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReportTemplateDelete = ({
  reportTemplate,
  showDialog,
  setShowDialog,
}: ReportTemplateDeleteProps) => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate } = useDeleteReportTemplate();
  const { toast } = useToast();

  const onDelete = () => {
    setIsSubmitting(true);
    const templateName = reportTemplate.tplName;

    if (!reportTemplate.tplUuid) {
      toast({
        className: 'delete-report-template-error-toast',
        title: messages.genericToastTitleError,
        description: messages.reportsDeleteTemplateFailedNoIdMessage.replace(
          '{label}',
          templateName,
        ),
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    mutate(reportTemplate.tplUuid, {
      onSuccess: () => {
        toast({
          className: 'delete-report-template-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDeleteTemplateSuccessMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: (error: Error) => {
        markErrorHandledLocally(error);
        let text = messages.reportsDeleteTemplateFailedGenericMessage.replace(
          '{label}',
          templateName,
        );

        if (error.message.includes('access denied')) {
          text =
            messages.reportsDeleteTemplateFailedMissingPermissionsMessage.replace(
              '{label}',
              templateName,
            );
        }

        toast({
          className: 'delete-report-template-error-toast',
          title: messages.genericToastTitleError,
          description: text,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
    });
  };

  return (
    <DialogForm showDialog={showDialog} onOpenChange={setShowDialog} size="sm">
      <DialogForm.Header>
        <DialogForm.Title>
          {messages.reportsDeleteTemplateTitle}
        </DialogForm.Title>
        <DialogForm.Description>
          {messages.reportsDeleteTemplateWarning}
        </DialogForm.Description>
      </DialogForm.Header>
      <DialogForm.Body>
        <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
          <Label className="font-light">
            {messages.reportsDeleteTemplateNameLabel}
          </Label>
          <Label htmlFor="report-template-name">{reportTemplate.tplName}</Label>
        </div>
      </DialogForm.Body>
      <DialogForm.Footer>
        <DialogForm.Cancel disabled={isSubmitting}>
          {messages.genericCancel}
        </DialogForm.Cancel>
        <DialogForm.Submit
          id="report-template-confirm-delete-button"
          variant="destructive"
          disabled={isSubmitting}
          onSubmit={onDelete}
        >
          {isSubmitting ? messages.genericDeleting : messages.genericDelete}
        </DialogForm.Submit>
      </DialogForm.Footer>
    </DialogForm>
  );
};
